import type {
  AutocompleteRenderGroupParams,
  TextFieldProps,
} from '@mui/material';
import {
  Autocomplete,
  ListSubheader,
  Avatar,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import { Fragment, forwardRef } from 'react';
import { tss } from 'tss-react/mui';

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';

import type { AvatarUser } from '@/modules/user/components/UserAvatar';
import { UserAvatar } from '@/modules/user/components/UserAvatar';
import { graphql, type DocumentType } from '@/gql';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { nonFalsey } from '@/utils/nonFalsey';
import { Or2ListItem } from '@/components/data-display/Or2ListItem';

const NOTIFICATION_CHIP_INPUT_GROUP_FRAGMENT = graphql(`
  fragment notificationChipInputGroupFields on Group {
    _id
    referenceName
    type {
      hasCommitteesWithGroupType {
        _id
        containsGroupsConnection(filterOn: { parents: [$groupId] }) {
          edges {
            node {
              _id
              name
            }
          }
        }
      }
    }
    membershipData {
      membersConnection(orderBy: [LASTNAME, FIRSTNAME]) {
        edges {
          node {
            _id
            user {
              _id
              firstName
              lastName
              email
              ...userImageFields
            }
          }
        }
      }
    }
  }
`);

const useStyles = tss.create(({ theme }) => {
  return {
    chipInputs: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    listAvatar: {
      marginRight: theme.spacing(2),
      height: 32,
      width: 32,
    },
    listbox: {
      padding: 0,
    },
    option: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),

      backgroundColor: 'transparent',
      // selected
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      // hover
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    chip: {
      overflow: 'hidden',
      maxWidth: '20rem',
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    inputRoot: {
      borderRadius: '20px',
    },
  };
});

export type NotificationItem =
  | {
      _id: string;
      displayName: string;
      type: 'Group' | 'All';
    }
  | {
      _id: string;
      displayName: string;
      type: 'User';
      avatarUser: AvatarUser;
    };

export interface NotificationAutocompleteGroup {
  name: string;
  type: 'All' | 'Group' | 'User';
}

interface Props {
  group: DocumentType<typeof NOTIFICATION_CHIP_INPUT_GROUP_FRAGMENT>;
  selectedOptions: NotificationItem[];
  loading: boolean;
  label?: string;
  placeholder?: string;
  textFieldProps?: Partial<TextFieldProps>;
  onChange: (selectedOptions: NotificationItem[]) => void;
}

export const NotificationChipInput = forwardRef<HTMLDivElement, Props>(
  (
    {
      group,
      selectedOptions,
      loading,
      label = 'Send email to',
      placeholder,
      textFieldProps,
      onChange,
    },
    ref,
  ) => {
    const { classes } = useStyles();

    const { referenceName, type, membershipData } = group;
    const { hasCommitteesWithGroupType } = type ?? {};
    const { containsGroupsConnection } = hasCommitteesWithGroupType ?? {};

    const { membersConnection } = membershipData;

    const committeeOptions = extractConnectionProps(containsGroupsConnection)
      .map((committee) => {
        if (!(committee._id && committee.name)) {
          return false;
        }

        return {
          _id: committee._id,
          displayName: committee.name,
          type: 'Group' as const,
        };
      })
      .filter(nonFalsey)
      .sort((a, b) => {
        const adn = a.displayName ?? '';
        const bdn = b.displayName ?? '';

        return adn.localeCompare(bdn);
      });

    const memberOptions = extractConnectionProps(membersConnection)
      .map((m) => {
        if (!(m.user && m.user._id)) {
          return false;
        }

        return {
          _id: m.user._id,
          displayName: m.user.firstName + ' ' + m.user.lastName,
          type: 'User' as const,
          avatarUser: {
            _id: m.user._id,
            firstName: m.user.firstName,
            lastName: m.user.lastName,
            profileImage: m.user.profileImage,
            image: m.user.image,
          },
        };
      })
      .filter(nonFalsey)
      .sort((a, b) => {
        const adn = a.displayName ?? '';
        const bdn = b.displayName ?? '';

        return adn.localeCompare(bdn);
      });

    const allSuggestion = {
      displayName: 'All members of the ' + referenceName,
      type: 'All' as const,
      _id: 'all',
    };

    const allOptions: NotificationItem[] = [
      allSuggestion,
      ...committeeOptions,
      ...memberOptions,
    ];

    const groups: NotificationAutocompleteGroup[] = [
      {
        type: 'All',
        name: 'All',
      },
      {
        type: 'Group',
        name: 'Committees',
      },
      {
        type: 'User',
        name: 'Members',
      },
    ];

    return (
      <Autocomplete
        disableCloseOnSelect
        multiple
        options={allOptions}
        value={selectedOptions}
        ref={ref}
        loading={loading}
        getOptionLabel={(option) => option.displayName ?? ''}
        getOptionKey={(option) => option._id}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        classes={{ option: classes.option, listbox: classes.listbox }}
        groupBy={(option) => option.type}
        renderGroup={(params: AutocompleteRenderGroupParams) => {
          const getMatchingGroup = groups.find(
            (g) => String(g.type) === String(params.group),
          );

          return (
            <Fragment key={params.key}>
              <ListSubheader>{getMatchingGroup?.name}</ListSubheader>
              {params.children}
            </Fragment>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          return (
            <Or2ListItem key={option._id} {...otherProps} dense>
              <ListItemAvatar>
                {option.type === 'User' ? (
                  <UserAvatar
                    user={option.avatarUser ?? null}
                    className={classes.listAvatar}
                    size={32}
                  />
                ) : (
                  <Avatar className={classes.listAvatar}>
                    {option.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                )}
              </ListItemAvatar>
              <ListItemText disableTypography>
                <Typography variant="subtitle1">
                  {option?.displayName}
                </Typography>
                {option.type === 'All' ? (
                  <Typography variant="caption">
                    This is the default if nothing else is selected
                  </Typography>
                ) : null}
              </ListItemText>
              {selected ||
              (selectedOptions.length === 0 && option.type === 'All') ? (
                <CheckBox fontSize="small" color="primary" />
              ) : (
                <CheckBoxOutlineBlank fontSize="small" color="primary" />
              )}
            </Or2ListItem>
          );
        }}
        fullWidth
        renderTags={(value: Array<Required<NotificationItem>>, getTagProps) => {
          return (
            <div className={classes.chipInputs}>
              {value.map(
                (option: Required<NotificationItem>, index: number) => (
                  <Chip
                    color="primary"
                    title={option.displayName}
                    label={option.displayName}
                    {...getTagProps({ index })}
                    key={option._id ?? index}
                    className={classes.chip}
                    avatar={
                      option.type === 'User' ? (
                        <UserAvatar
                          user={option.avatarUser ?? null}
                          size={24}
                        />
                      ) : (
                        <Avatar>
                          {option.displayName.charAt(0).toUpperCase()}
                        </Avatar>
                      )
                    }
                  />
                ),
              )}
            </div>
          );
        }}
        popupIcon={
          loading ? (
            <CircularProgress disableShrink size={16} />
          ) : (
            <ArrowDropDownIcon />
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            {...textFieldProps}
            disabled={loading}
            label={label}
            placeholder={
              selectedOptions.length === 0
                ? (placeholder ?? `All members of the ${referenceName}`)
                : undefined
            }
            helperText={
              selectedOptions.length > 0 ? (
                <>
                  This only restricts who gets emailed - whatever you select
                  above, the message will appear on the noticeboard below, and
                  will be visible for everyone to see
                </>
              ) : null
            }
            slotProps={{
              input: {
                ...params.InputProps,
                classes: {
                  root: classes.inputRoot,
                },
              },

              inputLabel: { shrink: true },
            }}
          />
        )}
        onChange={(_event, newValue) => {
          // If all selected, remove all
          if (newValue.some((v) => v._id === '' || v._id === 'all')) {
            onChange([]);
          } else {
            onChange(newValue);
          }
        }}
      />
    );
  },
);

NotificationChipInput.displayName = 'NotificationChipInput';
